import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { ArrowRight, Pill, UtensilsCrossed, FlaskConical, Sparkles, Factory, Cpu } from "lucide-react";

const Industries = () => {
  const industries = [
    {
      icon: Pill,
      title: "Pharmaceuticals",
      description: "Sterile processing and storage solutions for pharmaceutical manufacturing",
      requirements: [
        "USP Class VI compliance",
        "Electropolished surfaces", 
        "CIP/SIP compatibility",
        "Sterile welding techniques",
        "Material traceability"
      ],
      products: ["Sterile storage tanks", "Reactor vessels", "Buffer tanks", "WFI storage systems"],
      clients: [
        "Pharmaniaga Sdn Bhd",
        "CCM Pharmaceutical Division", 
        "Xepa-Soul Pattison Sdn Bhd",
        "Kotra Pharma Sdn Bhd",
        "Sanofi Aventis",
        "GSK",
        "Hovid Bhd",
        "MSD"
      ]
    },
    {
      icon: UtensilsCrossed,
      title: "Food & Beverage",
      description: "Hygienic processing equipment meeting the highest food safety standards",
      requirements: [
        "FDA approved materials",
        "Sanitary design principles",
        "Easy cleaning surfaces",
        "Hygienic welding",
        "HACCP compliance"
      ],
      products: ["Process tanks", "Storage vessels", "Mixing tanks", "Fermentation vessels"],
      clients: [
        "Cadbury",
        "Dumex", 
        "Fraser & Neave"
      ]
    },
    {
      icon: Cpu,
      title: "Semi Conductors",
      description: "Ultra-pure processing equipment for semiconductor manufacturing",
      requirements: [
        "Ultra-high purity materials",
        "Contamination control",
        "Precision manufacturing",
        "Clean room compatibility",
        "Chemical resistance"
      ],
      products: ["Process vessels", "Chemical storage tanks", "Ultra-pure systems", "Specialized equipment"],
      clients: ["Semiconductor manufacturers", "Technology companies"]
    },
    {
      icon: Factory,
      title: "General Industries",
      description: "Versatile solutions for diverse industrial applications and EPCC projects",
      requirements: [
        "Custom specifications",
        "Industrial grade quality",
        "Durability standards",
        "Cost effectiveness",
        "Project scalability"
      ],
      products: ["Storage tanks", "Process vessels", "Custom solutions", "Industrial systems"],
      clients: ["Majubina Bhd (EPCC Bitumen Plant)", "Various industrial clients"]
    }
  ];

  const keyClients = [
    {
      name: "Pharmaniaga",
      industry: "Pharmaceutical",
      projects: "Various Manufacturing Tank",
      description: "Malaysia's leading pharmaceutical company"
    },
    {
      name: "Xepa-Soul",
      industry: "Pharmaceutical", 
      projects: "Various Manufacturing Tank, RO water storage tank",
      description: "Pharmaceutical manufacturing specialist"
    },
    {
      name: "Majubina Bhd",
      industry: "Bitumen Manufacturing Plant",
      projects: "Phase 1 - 10+ Storage & Mixing Tank; Phase 2 - 7 x 150,000L Storage Tank; Hot Oil System, Piping with heat tracing",
      description: "EPCC turnkey project - we design and build a Bitumen Plant for Majubina"
    },
    {
      name: "CCM Pharmaceutical Division",
      industry: "Pharmaceutical",
      projects: "Various Manufacturing Tank",
      description: "Healthcare and pharmaceutical products division"
    },
    {
      name: "Prime Pharma",
      industry: "Pharmaceutical",
      projects: "Various Manufacturing Tank",
      description: "Pharmaceutical Manufacturing Plant specialist"
    }
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Industries We Serve | Pharmaceutical, F&B, Chemical - WLE Technology</title>
        <meta name="description" content="Tank fabrication solutions for pharmaceuticals, food & beverage, semiconductors, and general industries. USP Class VI, FDA, HACCP compliant. Serving Pharmaniaga, CCM, and more." />
        <link rel="canonical" href="https://wletec.com/industries" />
        <meta property="og:title" content="Industries We Serve - WLE Technology" />
        <meta property="og:description" content="Specialized tank solutions for pharmaceutical, F&B, and chemical industries." />
        <meta property="og:url" content="https://wletec.com/industries" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="pharmaceutical tanks, food grade tanks, chemical processing, semiconductor equipment, Malaysia" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-foreground mb-6">
              Industries We <span className="text-wle-blue">Serve</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From pharmaceuticals, food processing, Semi Conductors to General Industries
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16">
        <div className="container">
          <div className="space-y-12">
            {industries.map((industry, index) => (
              <Card key={index} className="overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-0">
                  <CardHeader className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-wle-blue/10">
                        <industry.icon className="h-8 w-8 text-wle-blue" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">{industry.title}</CardTitle>
                        <CardDescription className="text-base">{industry.description}</CardDescription>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold mb-3">Key Requirements</h4>
                        <ul className="space-y-2">
                          {industry.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-wle-blue flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold mb-3">Notable Clients</h4>
                        <div className="space-y-1">
                          {industry.clients.map((client, idx) => (
                            <p key={idx} className="text-sm text-wle-blue font-medium">{client}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="p-8 bg-muted/50">
                    <h4 className="font-semibold mb-4">Products & Solutions</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {industry.products.map((product, idx) => (
                        <div key={idx} className="p-3 bg-card rounded-lg border">
                          <p className="text-sm font-medium">{product}</p>
                        </div>
                      ))}
                    </div>
                    
                    <Button asChild className="w-full mt-6">
                      <Link to={
                        industry.title === "Pharmaceuticals" ? "/services" :
                        industry.title === "Food & Beverage" ? "/services" :
                        industry.title === "Semi Conductors" ? "/services" :
                        "/services"
                      }>
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Key Clients Section */}
      <section className="py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Our Key Clients</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {keyClients.map((client, index) => (
              <Card key={index} className="hover:shadow-blue transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-lg">{client.name}</CardTitle>
                  <CardDescription>{client.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Industry:</span>
                      <span className="text-sm text-muted-foreground">{client.industry}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Projects:</span>
                      <span className="text-sm text-wle-blue font-medium">{client.projects}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Standards */}
      <section className="py-16">
        <div className="container">
          <Card className="bg-gradient-primary text-white">
            <CardContent className="p-12">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">Industry Standards & Certifications</h2>
                <p className="text-lg opacity-90">
                  We maintain the highest standards across all industry sectors
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-4">Pharmaceutical</h3>
                  <ul className="space-y-2 text-sm opacity-90">
                    <li>• USP Class VI</li>
                    <li>• FDA Compliance</li>
                    <li>• ASME BPE</li>
                    <li>• GMP Standards</li>
                  </ul>
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-4">Food & Beverage</h3>
                  <ul className="space-y-2 text-sm opacity-90">
                    <li>• FDA Food Grade</li>
                    <li>• HACCP Compliance</li>
                    <li>• 3A Sanitary Standards</li>
                    <li>• ISO 22000</li>
                  </ul>
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-4">Chemical</h3>
                  <ul className="space-y-2 text-sm opacity-90">
                    <li>• ASME Section VIII</li>
                    <li>• PED Compliance</li>
                    <li>• NACE Standards</li>
                    <li>• API Specifications</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container">
          <Card>
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">Your Industry, Our Expertise</h3>
              <p className="text-lg text-muted-foreground mb-8">
                Don't see your specific industry listed? We work across many sectors and can adapt 
                our solutions to meet your unique requirements.
              </p>
              <Button asChild size="lg">
                <Link to="/contact">
                  Discuss Your Requirements
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Industries;